import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = ''

const CapabilitySections = ({ slug }) => {
    const [sections, setSections] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        let active = true
        axios.get(`${API}/api/capabilities/${slug}/sections`)
            .then(res => {
                if (active) {
                    setSections(res.data)
                    setLoading(false)
                }
            })
            .catch(err => {
                console.error(`Error fetching capabilities sections for ${slug}:`, err)
                if (active) setLoading(false)
            })
        return () => { active = false }
    }, [slug])

    // Reset active index when slug changes
    useEffect(() => {
        setActiveIndex(0)
    }, [slug])

    // Auto-rotate effect: switches slides every 5 seconds, resetting the timer on manual navigation
    useEffect(() => {
        if (sections.length <= 1) return

        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev === sections.length - 1 ? 0 : prev + 1))
        }, 5000)

        return () => clearInterval(timer)
    }, [activeIndex, sections.length])

    if (loading || sections.length === 0) return null

    const handlePrev = () => {
        setActiveIndex((prev) => (prev === 0 ? sections.length - 1 : prev - 1))
    }

    const handleNext = () => {
        setActiveIndex((prev) => (prev === sections.length - 1 ? 0 : prev + 1))
    }

    const currentSection = sections[activeIndex]

    return (
        <section className="cap-carousel-section" style={{ padding: 'clamp(2rem, 6vh, 5rem) 2.5rem', backgroundColor: 'var(--v2-bg-alt, #faf9f6)' }}>
            <div className="cap-carousel-container" style={{ maxWidth: 'min(900px, 92vw)', margin: '0 auto', position: 'relative' }}>
                
                {/* Carousel Image viewport */}
                <div className="cap-carousel-viewport" style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)', border: '1px solid var(--v2-border)', backgroundColor: '#000', height: 'clamp(200px, 42vh, 480px)' }}>
                    {sections.map((section, idx) => (
                        <div 
                            key={section.id || idx}
                            className={`cap-carousel-slide ${idx === activeIndex ? 'active' : ''}`}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: idx === activeIndex ? 1 : 0,
                                transition: 'opacity 0.6s ease-in-out',
                                zIndex: idx === activeIndex ? 2 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {section.image_path ? (
                                <img 
                                    src={`${API}${section.image_path}`} 
                                    className="cap-carousel-img" 
                                    alt={section.title} 
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            ) : (
                                <div className="cap-carousel-placeholder" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--v2-surface)', color: 'var(--v2-text3)' }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '12px' }}>📷</span>
                                    <span style={{ fontFamily: 'var(--pfc-mono, monospace)', fontSize: '12px', textTransform: 'uppercase' }}>Image Placeholder</span>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Navigation Arrows overlay */}
                    {sections.length > 1 && (
                        <>
                            <button 
                                onClick={handlePrev}
                                className="cap-carousel-arrow arrow-left"
                                aria-label="Previous slide"
                                style={{
                                    position: 'absolute',
                                    left: '16px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 10,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'
                                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <button 
                                onClick={handleNext}
                                className="cap-carousel-arrow arrow-right"
                                aria-label="Next slide"
                                style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 10,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    outline: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'
                                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>

                {/* Dot Indicators */}
                {sections.length > 1 && (
                    <div className="cap-carousel-dots" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1.5rem' }}>
                        {sections.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`cap-carousel-dot ${idx === activeIndex ? 'active' : ''}`}
                                aria-label={`Go to slide ${idx + 1}`}
                                style={{
                                    width: idx === activeIndex ? '24px' : '8px',
                                    height: '8px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    background: idx === activeIndex ? 'var(--v2-primary, #26215C)' : 'var(--v2-border, #e0e0e0)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    padding: 0
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* Caption / Content - short descriptor/title under the image */}
                <div 
                    className="cap-carousel-caption" 
                    style={{ 
                        marginTop: '2.5rem', 
                        textAlign: 'center'
                    }}
                >
                    <h3 className="v2-content-title" style={{ fontWeight: 800, fontSize: 'clamp(1.25rem, 2.2vw, 1.75rem)', marginBottom: '0.5rem', color: 'var(--v2-primary, #26215C)' }}>
                        {currentSection.title}
                    </h3>
                    {currentSection.body && (
                        <p style={{ maxWidth: '640px', margin: '0 auto', fontSize: '1rem', lineHeight: 1.7, color: 'var(--v2-text2)' }}>
                            {currentSection.body}
                        </p>
                    )}
                </div>

            </div>
        </section>
    )
}

export default CapabilitySections
